/**
 * Safeguarding: a message that sounds like a child is hurt, unsafe, at risk of self-harm or missing.
 * Checked before anything else, including while a draft is open, so it never becomes a Notice or a
 * draft's text. It errs towards flagging: a false alarm costs one extra reply, a miss can cost a child.
 */

/** Always a concern, whoever it's about. */
const ALWAYS = [
  /\bsafeguarding\b(?!\s+(?:training|policy|week|officer'?s?\s+(?:office|email|number)))/,
  /\bself[\s-]?harm\w*/,
  /\bsuicid\w*/,
  /\b(?:kill|killing|hurt|hurting|harm|harming|cut|cutting|burn|burning|starve|starving)\s+(?:myself|himself|herself|themselves|themself|themselfs|themselfes)\b/,
  /\bend(?:ing|ed)?\s+(?:my|his|her|their)\s+(?:own\s+)?li(?:fe|ves)\b/,
  /\b(?:want|wants|wanted|wanna)\s+to\s+die\b/,
  /\b(?:don'?t|doesn'?t|do\s+not|does\s+not)\s+want\s+to\s+(?:be\s+alive|live)\b/,
  /\boverdos\w*/,
  /\brap(?:e|ed|ing|ist)\b/,
  /\bsexual(?:ly)?\s+(?:abus|assault|harass|touch)\w*/,
  /\bmolest\w*/,
  /\b(?:abuse|abused|abusing|abusive)\b(?!\s+(?:of\s+(?:the\s+)?(?:system|app|platform|privileges?)|report\s+(?:button|feature)))/,
  /\bnot\s+safe\s+(?:at\s+home|to\s+go\s+home)\b/,
  /\b(?:scared|afraid|frightened|terrified)\s+(?:to\s+go\s+home|of\s+(?:his|her|their|my)\s+(?:dad|father|mum|mom|mother|step\w*|uncle|aunt|parents?|brother|sister|grand\w*|boyfriend|girlfriend))\b/,
];

/** Someone under 18, however they're described. */
const CHILD = /\b(?:child|children|kid|kids|pupil|pupils|learner|learners|student|students|boy|boys|girl|girls|son|daughter|baby|toddler|teen|teenager|minor|grade\s*(?:\d+|r)s?|twins|little|kind|kinders|leerders?|laaitie|umntwana|ingane|umfundi|\d{1,2}[ -]year[ -]old|(?:eight|nine|ten|eleven|twelve)[ -]year[ -]old|grade\s*\w+\s+(?:learner|child|boy|girl|pupil))\b/;

/** Harm, said about a child. */
const DANGER =
  /\b(?:hurt|hurts|hurting|harm|harmed|harming|injur\w*|unsafe|missing|bruis\w*|hit|hits|hitting|beat|beats|beaten|beating|slap\w*|punch\w*|kick(?:s|ed|ing)?|chok\w*|strangl\w*|touch(?:es|ed|ing|d)?|grop\w*|grabb?\w*|burn(?:s|ed|t)?|marks?\s+on\s+(?:his|her|their|the)\s+(?:neck|arms?|face|body|legs?|back|wrists?|skin)|neglect\w*|bull(?:ied|ying)\s+and\s+\w+|kidnap\w*|abduct\w*|assault\w*|threat\w*|groom(?:ed|ing)|forced|locked|drunk|intoxicated|high|weapon|knife|gun|(?:ran|run|runs|running)\s+away(?=\s+(?:from|last|again|yesterday|today|this|and))|followed|following|inappropriate|unfed|hasn'?t\s+been\s+fed|not\s+(?:been\s+)?fed|left\s+alone)\b/;

/** An adult or older person who could be doing the harm. */
const ACTOR =
  /\b(?:dad|daddy|father|stepdad|stepfather|step-dad|step-father|mum|mom|mommy|mummy|mother|stepmum|stepmom|stepmother|uncle|aunt|aunty|auntie|grandpa|grandfather|grandad|granny|grandma|grandmother|boyfriend|girlfriend|brother|sister|cousin|neighbou?r|teacher|coach|driver|someone|somebody|a\s+man|a\s+woman|an\s+adult|older\s+(?:boy|girl|kid)|gran|gogo|oupa|oom|malume|umalume|umkhulu|mkhulu|boet|stiefpa|karel|pa|ma|parents?)\b/;

/** Violence or abuse done to "him", "her" or "them": a named child is still a child. */
const DONE_TO_THEM =
  /\b(?:hurts?|hurting|hit|hits|hitting|beat|beats|beating|beaten|slaps?|slapping|slapped|punch(?:es|ed|ing)?|kicks?|kicking|kicked|chokes?|choking|touch(?:es|ed|ing|d)?|grabb?(?:ed|s|ing)?|burns?|burned|burnt|locks?|locked|threaten(?:s|ed|ing)?|forced|abuses?|abused)\s+(?:him|her|them|me)\b/;

/** "the boys hurt their legs at rugby": an injury at sport or play, not harm by someone. */
const SPORTS_INJURY =
  /\b(?:hurt|injured|bruised)\s+(?:their|his|her)\s+(?:legs?|arms?|knees?|ankles?|wrists?|backs?|heads?|fingers?|feet|foot)\b.*\b(?:rugby|soccer|football|netball|hockey|cricket|sport|sports|pe|practice|match|game|playground|athletics|gala)\b/;

/** A list question ("which students are missing their slips"), not a report that one child is missing. */
const PRODUCT_LIST = /\b(?:which|how many|show(?: me)?|count|list)\b/;
const PRODUCT_GROUP = /\b(?:students?|pupils?|learners?|children|kids?|users?|notices?|drafts?|invitations?|invites?|grades?|classes?|circles?)\b/;
/** "Hit the button", "hit publish", "hit 30 degrees": not a child being hit. */
const PRODUCT_HIT = /\bhit(?:s|ting)?\s+(?:the\s+)?(?:\w+\s+)?(?:button|send|publish|enter|save|degrees|target|total)\b|\bhit\s+\d+|\bhit count\b/;
/** "children must not bring knives", "high school", "a high five": a rule or a phrase, not a danger. */
const HARMLESS = /\b(?:must\s+not|mustn'?t|don'?t|no)\s+bring\s+(?:knives|knife|weapons?)\b|\bhigh\s+(?:school|five|chair|jump|tea|score|season)\b|\bmarks?\s+(?:sheet|book|out\s+of|for|on\s+the\s+test)\b|\btest\s+marks?\b|\bfinal\s+touches\b|\btouch\s+base\b/;

/** Fixes the slips people make typing in a hurry, so "touchd" and "themselfs" still read. */
// Adjacent transpositions in a small risk vocabulary; ordinary words are left intact.
const RISK_WORDS = ["child", "children", "girls", "twins", "adult", "abused", "attacked", "scratching", "slaan"];
const TRANSPOSED = new Map<string,string>();
for (const word of RISK_WORDS) {
  for (let i=0;i<word.length-1;i+=1) {
    const typo=word.slice(0,i)+word[i+1]+word[i]+word.slice(i+2);
    if(typo!==word) TRANSPOSED.set(typo,word);
  }
}
function normalise(text: string): string {
  return ` ${text
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[’`]/g, "'")
    .replace(/[^a-z0-9'\s-]+/g, " ")
    .replace(/\b[a-z]+\b/g, word=>TRANSPOSED.get(word) ?? word)
    .replace(/\s+/g, " ")
    .trim()} `;
}

/** A child word and a danger word in the same message, minus the product phrases that only look alike. */
function childInDanger(said: string): boolean {
  if (!CHILD.test(said)) return false;
  let rest = said.replace(HARMLESS, " ").replace(SPORTS_INJURY, " ");
  if (PRODUCT_HIT.test(rest)) rest = rest.replace(/\bhit(?:s|ting)?\b/g, " ");
  if (PRODUCT_LIST.test(rest) && PRODUCT_GROUP.test(rest)) rest = rest.replace(/\bmissing\b/g, " ");
  if (/\bmissing\s+(?:notices?|drafts?|users?|invitations?|invites?|forms?|slips?|permission)\b/.test(rest)) rest = rest.replace(/\bmissing\b/g, " ");
  // "followed" and "following" are only a danger when someone is following a child
  if (/\bfollow(?:ed|ing)\b/.test(rest) && !/\bfollow(?:ed|ing)\s+(?:one\s+of\s+)?(?:our|my|the|a|him|her|them)\b[^.?!]*\b(?:home|school|gate|bus)\b/.test(rest)) {
    rest = rest.replace(/\bfollow(?:ed|ing)\b/g, " ");
  }
  return DANGER.test(rest);
}

/** Harmless objects and activities are removed locally, never as an exemption for the whole message. */
function riskText(said: string): string {
  return said
    .replace(/\bhit (?:their|his|her|our) (?:reading )?target\b/g, " ")
    .replace(/\b(?:jersey|lunchbox|books?)\b[^.!?]{0,25}\bmissing\b/g, " ")
    .replace(/\b(?:child\s+)?abuse\s+(?:awareness|prevention)(?:\s+(?:walk|talk|workshop|training))?/g, " ")
    .replace(/\bself[ -]?harm\s+(?:awareness|prevention|training)(?:\s+(?:talk|workshop))?/g, " ")
    .replace(/\babus(?:e|es|ed|ing|ive)\b(?=.{0,35}\b(?:parking|bays|wifi|patience)\b)/g, " ")
    .replace(/\b(?:a child's |his |her |their )?(?:navy )?(?:jersey|lunch ?box|books?|slips?|forms?)\b.{0,12}\b(?:went |gone )?missing\b/g, " ")
    .replace(/\bmissing (?:their |his |her |a )?(?:library books?|jerseys?|lunch ?boxes|permission slips?|forms?|mommies|moms)\b/g, " ")
    .replace(/\b(?:beat|beaten)\b(?:\s+[a-z]+){0,3}\s+\d+[- ]\d+\b/g, " ")
    .replace(/\bbeat (?!(?:him|her|them|me|a|the|my|our)\b)(?:[a-z'-]+\s+){1,4}at (?:soccer|rugby|netball|chess|hockey)\b(?!\s+(?:practice|training))/g, " ")
    .replace(/\b(?:butts kicked at chess|beaten by the grade \d+s at netball|beat the grade \d+s at tug of war)\b/g, " ")
    .replace(/\b(?:touch(?:ed|ing)? (?:the )?class snake|kick(?:ing|ed)? (?:the )?ball|forced (?:the )?storeroom door open|high (?:on sugar|energy)|knife and fork|punched above their weight|hit the ground running)\b/g, " ");
}

/** Patterns describe a risk and its context, rather than matching an entire disclosure sentence. */
const CONTEXTUAL_RISK = [
  /\b(?:pretending|claiming) to be a social worker\b.{0,35}\b(?:take|collect|fetch)|\b(?:someone|stranger) tried to get\b.{0,35}\b(?:his|a|the) car\b/,
  /\b(?:staff members?|adult)\b.{0,55}\b(?:messaging|send pics|late at night)|\bstrange car\b.{0,50}\bfollowing\b/,
  /\b(?:smacks? (?:her|him|them)|something very sexual|dirty clothes every day|baby sister alone|beaten\b.{0,40}\bclassmates)\b/,
  /\b(?:can'?t|cannot) find\b.{0,35}\b(?:didn'?t|did not) come back\b/,

  // Sexual exploitation, coercion and requests for secrecy.
  /\b(?:naked|nude|private|adult|inappropriate|sexual)\s+(?:photos?|pictures?|pics|videos?|parts?|stuff|things?)\b/,
  /\b(?:blackmail\w*|sextort\w*)\b/,
  /\b(?:take|remove|taking)\b.{0,25}\b(?:clothes|top)\s+off|\bremove\s+(?:her|his|their)\s+clothes\b/,
  /\b(?:comes?|visits?|sleeps?|sleeping)\b.{0,30}\b(?:room|bed)\b.{0,35}\b(?:night|hurt|bad things|does things)|\b(?:night)\b.{0,30}\b(?:does things|bad things)\b/,
  /\b(?:made|makes?|forced)\b.{0,35}\b(?:do things|watch adult|keep secrets? about touching|sit on his lap)\b/,
  /\b(?:does?|did|doing) (?:bad |inappropriate )?things to (?:her|him|them|me)\b/,
  /\b(?:kisses?|kissed)\b.{0,25}\bmouth\b/,
  /\b(?:married off|sells? (?:her|him|them) to men|pregnant)\b/,
  // Neglect, abandonment and unmet basic needs.
  /\b(?:hasn'?t|haven'?t|not|never)\s+(?:had\s+)?(?:eaten|eating|fed|lunch)\b|\bno (?:food|bed|medicine)\b|\buntreated wound\b/,
  /\b(?:left|alone|by (?:herself|himself|themselves))\b.{0,45}\b(?:home|overnight|all weekend|every night)|\bhome\b.{0,30}\b(?:alone|by (?:herself|himself|themselves))\b/,
  /\b(?:sleep|sleeps|sleeping)\b.{0,30}\b(?:outside|floor in the cold)|\b(?:bins at break|dirty clothes every day|strangers for days|work at a shebeen)\b/,
  // Self-harm signs, including euphemisms and indirect intent.
  /\b(?:cuts?|scratching|cutting)\b.{0,35}\b(?:arms?|wrists?|bleed|again)|\b(?:better off without|ending it all|disappear forever|not wake up|wishes she was dead|rather die|saying goodbye)\b/,
  /\b(?:lot of pills|jump off (?:the |a )?bridge)\b/,
  // Uncollected or missing children and unsafe collection.
  /\b(?:nobody|no one)\b.{0,30}\b(?:fetch|collect)|\b(?:not|wasn'?t|hasn'?t been)\s+(?:collected|fetched)|\bdidn'?t pick (?:him|her|them) up\b/,
  /\b(?:can'?t|cannot)\s+find\b.{0,30}\b(?:learner|child)|\bnever made it home|\b(?:didn'?t come back|(?:not|isn'?t|isnt) on the collection list|protection order|nobody knows where)\b/,
  /\b(?:still here|waiting at the gate|walks home alone)\b.{0,55}\b(?:phones are off|getting dark|after dark|nobody)|\b(?:lost a child|aftercare closed)\b/,
  // Grooming, threatening adults, dangerous injuries and domestic violence.
  /\b(?:stranger|man|adult)\b.{0,65}\b(?:offering sweets|offered sweets|taking photos|calling the kids|into his car|wants to meet|late at night)|\boffered sweets\b.{0,30}\bstranger\b/,
  /\b(?:adult|staff member|guy|man)\b.{0,70}\b(?:messaging|send pics|pick her up|not to tell)|\b(?:secret about what an adult|not allowed to tell anyone|child protection issue|something (?:really )?worrying|something awful)\b/,
  /\b(?:black eye|belt marks|finger marks|burn mark|swollen face|covered in bruises|cut on his head|pushed down the stairs|stabbed|attacked)\b/,
  /\b(?:sleep outside|hurt mommy|dad hits her mom|drinks and shouts|doesn'?t feel safe)\b/,
];
const LOCAL_RISK = /\b(?:slaan|geslaan|uyamshaya|uyayihlukumeza|ukuzibulala|sjambok|geen kos)\b/;

/**
 * Does this message sound like a child is hurt, unsafe, at risk or missing?
 * Checked before any other matching, so these never get a normal reply.
 */
export function isSafeguardingConcern(text: string): boolean {
  const said = riskText(normalise(text));
  if (ALWAYS.some((pattern) => pattern.test(said))) return true;
  const childContext = CHILD.test(said) || /\bschool|class\b/.test(said) || /\bgrade\s*\d+s?\b|\b(?:he|she|her|his|him|they|them)\b/.test(said);
  if (LOCAL_RISK.test(said)) return true;
  if (childContext && CONTEXTUAL_RISK.some(pattern => pattern.test(said))) return true;
  if (childInDanger(said)) return true;
  // "Thabo's stepdad is beating him", "her uncle hurts her": someone doing harm to a child named or not
  if (DONE_TO_THEM.test(said.replace(HARMLESS, " ")) && (ACTOR.test(said) || CHILD.test(said) || /\b(?:told|tells|said|says)\s+(?:me|us)\b/.test(said))) return true;
  // "a child disclosed something worrying", "a learner confided": a disclosure, even before the details
  if (CHILD.test(said) && /\b(?:disclos\w*|confided|confides)\b/.test(said)) return true;
  return false;
}

export const SAFEGUARDING_REPLY =
  "This sounds serious, and it isn't something I can help with. " +
  "Please follow your Organization's safeguarding procedure now and tell the person responsible for safeguarding. " +
  "If a child is in immediate danger, call the emergency services.";

/** Right after a safeguarding reply, a question about telling others: never through a Notice. */
export const SAFEGUARDING_SHARING_REPLY =
  "Please don't share this in a Notice or with other parents. Keep it private and tell the person responsible for safeguarding now, " +
  "following your Organization's safeguarding procedure. If a child is in immediate danger, call the emergency services.";

/**
 * "should I post a notice about it to everyone?", "can I tell the parents?": about sharing what was just reported.
 * "tell me a joke" or "what's a circle?" is a new question, answered as usual.
 */
export const ASKS_ABOUT_SHARING =
  /\b(?:post|put|send|share|tell|message|announce|forward|warn|let)\b(?!\s+me\b)[^.?!]*\b(?:notices?|everyone|parents|families|whatsapp|group|circle|class|school|others|staff|it|this|that|them)\b|\bnotice\b[^.?!]*\b(?:about|on)\s+(?:it|this|that)\b/i;

/** A follow-up about the disclosure, rather than any new request to send a Notice. */
export function asksToShareSafeguardingConcern(text: string): boolean {
  if (!ASKS_ABOUT_SHARING.test(text)) return false;
  if (/^(?:will you send it without me checking|how many notices did we send this week)[?.!]*$/i.test(text.trim())) return false;
  if (/\babout\s+(?:it|this|that|what happened|the (?:incident|disclosure|man at the gate))\b/i.test(text)) return true;
  if (/\babout\s+\S+/i.test(text)) return false;
  if (/\bhow many\b|\b(?:how|where) (?:do|can|should) i (?:send|create|make|schedule)\b/i.test(text)) return false;
  if (/\b(?:share|post|send|forward)\s+(?:it|this|that)\b/i.test(text)) return true;
  return /\b(?:should|must|can|could|do)\s+(?:i|we|you)\b/i.test(text) || /\b(?:put it on|tell the other|tell the whole)\b/i.test(text);
}
